'use client';

import { X } from 'lucide-react';
import Image from 'next/image';
import { useEffect } from 'react';

export type FilePreviewData = {
  id: string;
  file: File;
  url: string;
  name: string;
  s3Key?: string; // Add optional s3Key for Supabase assets
};

type FilePreviewProps = {
  file: FilePreviewData | null;
  onClose: () => void;
};

export function FilePreview({ file, onClose }: FilePreviewProps) {
  // Close preview on Escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && file) {
        onClose();
      }
    };

    if (file) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [file, onClose]);

  if (!file || !file.url) {
    return null;
  }

  const isPdf = file.file.type === 'application/pdf';

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 transition-opacity duration-300 ease-in-out"
      role="dialog"
      aria-modal="true"
      aria-labelledby="preview-title"
    >
      {/* Invisible button that covers the entire overlay for closing */}
      <button
        className="absolute inset-0 w-full h-full cursor-default focus:outline-none"
        onClick={onClose}
        aria-label="Close preview"
        tabIndex={0}
      />

      {/* Dialog container */}
      <div
        className="relative bg-white rounded-lg shadow-xl w-full max-w-md max-h-[80vh] overflow-hidden z-10 transition-transform duration-300 ease-in-out transform"
        role="document"
        style={{ transform: 'translateY(0)' }}
      >
        <div className="p-3 flex justify-between items-center border-b">
          <h3 id="preview-title" className="text-sm font-medium truncate max-w-[250px]">
            {file.name}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full h-7 w-7 flex items-center justify-center hover:bg-gray-100 transition-colors"
            aria-label="Close preview"
          >
            <X className="h-4 w-4 text-gray-700" />
          </button>
        </div>
        <div className="p-4 flex items-center justify-center bg-gray-50">
          {isPdf ? (
            // PDF preview using iframe
            <iframe
              src={file.url}
              title={file.name}
              className="w-full object-contain"
              style={{
                height: 'calc(80vh - 70px)',
                border: 'none',
              }}
            />
          ) : (
            // Image preview
            <Image
              src={file.url}
              alt={file.name}
              width={800}
              height={600}
              className="max-w-full object-contain"
              style={{ maxHeight: 'calc(80vh - 70px)' }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// Export the legacy name for backward compatibility
export const ImagePreview = FilePreview;
